/**
 * O DEPÓSITO dos `resultados` da simulação criada pela conversa (spec-007).
 *
 * Colaborador do `apiFeeFixoIa`, que o instancia e delega: o captador o chama
 * ao chegar um preview NOVO (drawer e guia), o embrulho do chat imersivo o
 * chama com os `inputs` do payload, e o clique de Salvar passa por ele de novo
 * só para repetir um automático que falhou. A marca de sucesso mora na store
 * (`store/ia-deposito.js`), então remount de cartão não reposta; o POST em
 * voo fica na instância, para um clique durante o automático reaproveitar a
 * mesma promessa em vez de abrir um segundo POST.
 *
 * Mora em arquivo próprio porque `apiFeeFixoIa.js` já passava de 900 linhas e
 * o trio (guardas → POST → marca na store) é uma responsabilidade só.
 */
import APITemplate from '@api/APITemplate'
import store from '@state/store'
import { apiFeeFixoSimulacaoDeposito } from '@modules/fee-fixo/api/apiFeeFixoSimulacaoDeposito'
import { simulacaoDoChat } from '@modules/fee-fixo/utils/simulacao-do-chat'
import {
  MSG_ERRO_DEPOSITO_AUTOMATICO,
  MSG_PREVIEW_NAO_CORRENTE,
} from '@modules/fee-fixo/constants/guia'

class APIFeeFixoIaDeposito extends APITemplate {
  /**
   * @param {Object} opts
   * @param {string} opts.storeNamespace Namespace Vuex da conversa (`feeFixoIa`).
   */
  constructor({ storeNamespace }) {
    super()
    this.storeNamespace = storeNamespace
    this._depositoEmVoo = null
  }

  _getter(nome) {
    return store.getters[this.storeNamespace + '/' + nome]
  }

  _recusa(message) {
    return Promise.resolve(
      this.buildErrorResponse({ status: 400, message, value: null })
    )
  }

  /**
   * Deposita os números de um preview — idempotente por `previewId`.
   *
   * @param {Object} params
   * @param {string} params.previewId
   * @param {?Object} params.inputs O `S` do preview. Sem ele, só o preview
   *   CORRENTE da store é aceito (drawer e guia); com ele, o chat imersivo
   *   deposita sem escrever a pendência, que é UI das outras superfícies.
   * @returns {Promise<{status: number, message: string, value: *}>}
   */
  depositar({ previewId, inputs } = {}) {
    const preview = inputs ? { inputs } : this._pendenciaDe(previewId)
    if (!previewId || !preview) return this._recusa(MSG_PREVIEW_NAO_CORRENTE)
    if (this._getter('previewIdDepositado') === previewId) {
      return Promise.resolve(this.buildSuccessResponse({ value: null, message: '' }))
    }
    if (this._depositoEmVoo && this._depositoEmVoo.previewId === previewId) {
      return this._depositoEmVoo.promessa
    }
    const { publicavel, resultado } = simulacaoDoChat(preview.inputs)
    if (!publicavel) return this._recusa(MSG_ERRO_DEPOSITO_AUTOMATICO)
    const promessa = this._executar(previewId, resultado)
    this._depositoEmVoo = { previewId, promessa }
    return promessa
  }

  /** A pendência da store, se for deste `previewId` — senão `null`. */
  _pendenciaDe(previewId) {
    const pendente = this._getter('simulacaoPendente')
    return pendente && pendente.previewId === previewId ? pendente : null
  }

  _gravarDesfecho(previewId, r) {
    const ok = Boolean(r) && r.status === 200
    store.dispatch(this.storeNamespace + '/setDepositoDaSimulacao', {
      previewId,
      ok,
      erro: ok ? '' : (r && r.message) || MSG_ERRO_DEPOSITO_AUTOMATICO,
    })
    return r || this.buildErrorResponse({ message: MSG_ERRO_DEPOSITO_AUTOMATICO, value: null })
  }

  async _executar(previewId, resultados) {
    try {
      const r = await apiFeeFixoSimulacaoDeposito.depositar({ previewId, resultados })
      return this._gravarDesfecho(previewId, r)
    } catch {
      // Sem isto uma rejeição não gravava `ok: false` — cartão sem erro e sem
      // "Tentar de novo", mais um unhandled rejection no console.
      return this._gravarDesfecho(previewId, null)
    } finally {
      if (this._depositoEmVoo && this._depositoEmVoo.previewId === previewId) {
        this._depositoEmVoo = null
      }
    }
  }
}

export { APIFeeFixoIaDeposito }
